/**
 * Currency Conversion Service
 * Uses Exchange Rate API for live currency conversion
 * Supports cash+points calculations in multiple currencies
 */

export interface CurrencyRate {
  code: string;
  name: string;
  symbol: string;
  rate: number;
}

export interface ConvertedPrice {
  original: {
    amount: number;
    currency: string;
  };
  converted: {
    amount: number;
    currency: string;
    formatted: string;
  };
  rate: number;
  lastUpdated: Date;
}

export interface CashPointsPrice {
  points: number;
  cash: ConvertedPrice;
  totalValueInTargetCurrency: string;
  pointsValue: number; // Value of points in target currency
}

class CurrencyService {
  private static readonly EXCHANGE_API_KEY = process.env.REACT_APP_EXCHANGE_RATE_API_KEY;
  private static readonly BASE_URL = 'https://v6.exchangerate-api.com/v6';
  
  private static cache: Map<string, { rates: any; timestamp: number }> = new Map();
  private static readonly CACHE_DURATION = 30 * 60 * 1000; // 30 minutes

  /**
   * Popular currencies with symbols
   */
  static readonly SUPPORTED_CURRENCIES = [
    { code: 'USD', name: 'US Dollar', symbol: '$' },
    { code: 'EUR', name: 'Euro', symbol: '€' },
    { code: 'GBP', name: 'British Pound', symbol: '£' },
    { code: 'INR', name: 'Indian Rupee', symbol: '₹' },
    { code: 'JPY', name: 'Japanese Yen', symbol: '¥' },
    { code: 'AUD', name: 'Australian Dollar', symbol: 'A$' },
    { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$' },
    { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF' },
    { code: 'CNY', name: 'Chinese Yuan', symbol: '¥' },
    { code: 'SGD', name: 'Singapore Dollar', symbol: 'S$' },
    { code: 'AED', name: 'UAE Dirham', symbol: 'AED' },
    { code: 'SAR', name: 'Saudi Riyal', symbol: 'SR' },
  ];

  /**
   * Get exchange rates for a base currency
   */
  static async getExchangeRates(baseCurrency: string = 'USD'): Promise<any> {
    if (!this.EXCHANGE_API_KEY) {
      console.warn('⚠️ Exchange Rate API key not configured');
      return this.getFallbackRates();
    }

    const cacheKey = baseCurrency;
    const cached = this.cache.get(cacheKey);
    
    if (cached && (Date.now() - cached.timestamp) < this.CACHE_DURATION) {
      return cached.rates;
    }

    try {
      console.log(`💱 Fetching exchange rates for ${baseCurrency}...`);
      
      const response = await fetch(
        `${this.BASE_URL}/${this.EXCHANGE_API_KEY}/latest/${baseCurrency}`
      );

      if (!response.ok) {
        throw new Error(`Exchange Rate API error: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.result !== 'success') {
        throw new Error(`Exchange Rate API error: ${data.error_type}`);
      }

      // Cache the results
      this.cache.set(cacheKey, {
        rates: data,
        timestamp: Date.now(),
      });

      console.log(`✅ Exchange rates updated for ${baseCurrency}`);
      return data;
      
    } catch (error) {
      console.error('❌ Exchange Rate API failed:', error);
      return this.getFallbackRates();
    }
  }

  /**
   * Convert amount from one currency to another
   */
  static async convertCurrency(
    amount: number,
    fromCurrency: string,
    toCurrency: string
  ): Promise<ConvertedPrice> {
    if (fromCurrency === toCurrency) {
      return {
        original: { amount, currency: fromCurrency },
        converted: { 
          amount, 
          currency: toCurrency, 
          formatted: this.formatCurrency(amount, toCurrency) 
        },
        rate: 1,
        lastUpdated: new Date(),
      };
    }

    try {
      const rates = await this.getExchangeRates(fromCurrency);
      const rate = rates.conversion_rates[toCurrency];
      
      if (!rate) {
        throw new Error(`Currency ${toCurrency} not supported`);
      }

      const convertedAmount = amount * rate;

      return {
        original: { amount, currency: fromCurrency },
        converted: { 
          amount: convertedAmount, 
          currency: toCurrency,
          formatted: this.formatCurrency(convertedAmount, toCurrency)
        },
        rate,
        lastUpdated: new Date(rates.time_last_update_utc),
      };
      
    } catch (error) {
      console.error('Currency conversion failed:', error);
      // Fallback to approximate conversion
      return this.getFallbackConversion(amount, fromCurrency, toCurrency);
    }
  }

  /**
   * Convert cash+points pricing to target currency
   */
  static async convertCashPointsPrice(
    points: number,
    cashAmount: number,
    cashCurrency: string,
    targetCurrency: string,
    pointsValuePerPoint: number = 0.01 // Default: 1 cent per point
  ): Promise<CashPointsPrice> {
    const convertedCash = await this.convertCurrency(cashAmount, cashCurrency, targetCurrency);
    const pointsValueInTargetCurrency = await this.convertCurrency(
      points * pointsValuePerPoint, 
      'USD', 
      targetCurrency
    );

    const totalValue = convertedCash.converted.amount + pointsValueInTargetCurrency.converted.amount;

    return {
      points,
      cash: convertedCash,
      totalValueInTargetCurrency: this.formatCurrency(totalValue, targetCurrency),
      pointsValue: pointsValueInTargetCurrency.converted.amount,
    };
  }

  /**
   * Format currency amount with proper symbols
   */
  static formatCurrency(amount: number, currency: string): string {
    const currencyInfo = this.SUPPORTED_CURRENCIES.find(c => c.code === currency);
    const symbol = currencyInfo?.symbol || currency;
    
    // Format with appropriate decimal places
    const decimals = currency === 'JPY' || currency === 'KRW' ? 0 : 2;
    const formatted = amount.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });

    return `${symbol}${formatted}`;
  }

  /**
   * Get currency info by code
   */
  static getCurrencyInfo(code: string) {
    return this.SUPPORTED_CURRENCIES.find(c => c.code === code);
  }

  /**
   * Fallback rates when API is unavailable
   */
  private static getFallbackRates() {
    console.log('📊 Using fallback exchange rates');
    return {
      result: 'success',
      base_code: 'USD',
      conversion_rates: {
        USD: 1,
        EUR: 0.85,
        GBP: 0.73,
        INR: 83.15,
        JPY: 110.25,
        AUD: 1.35,
        CAD: 1.25,
        CHF: 0.92,
        CNY: 6.45,
        SGD: 1.35,
        AED: 3.67,
        SAR: 3.75,
      } as { [key: string]: number },
      time_last_update_utc: new Date().toISOString(),
    };
  }

  /**
   * Fallback conversion when API fails
   */
  private static getFallbackConversion(
    amount: number,
    fromCurrency: string,
    toCurrency: string
  ): ConvertedPrice {
    const fallbackRates = this.getFallbackRates();
    const rate = fallbackRates.conversion_rates[toCurrency] / fallbackRates.conversion_rates[fromCurrency];
    const convertedAmount = amount * rate;

    return {
      original: { amount, currency: fromCurrency },
      converted: { 
        amount: convertedAmount, 
        currency: toCurrency,
        formatted: this.formatCurrency(convertedAmount, toCurrency)
      },
      rate,
      lastUpdated: new Date(),
    };
  }

  /**
   * Check if currency service is configured
   */
  static isConfigured(): boolean {
    return !!this.EXCHANGE_API_KEY;
  }

  /**
   * Get popular currency pairs for a region
   */
  static getRegionalCurrencies(region: 'asia' | 'europe' | 'americas' | 'middle_east'): typeof this.SUPPORTED_CURRENCIES {
    const regions = {
      asia: ['USD', 'INR', 'JPY', 'CNY', 'SGD'],
      europe: ['EUR', 'GBP', 'CHF', 'USD'],
      americas: ['USD', 'CAD', 'EUR'],
      middle_east: ['USD', 'AED', 'SAR', 'EUR'],
    };

    const codes = regions[region] || ['USD', 'EUR'];
    return this.SUPPORTED_CURRENCIES.filter(c => codes.includes(c.code));
  }
}

export default CurrencyService;